import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu, X, Music4, Guitar, ArrowRight, Check, Star, CalendarClock, Library, Radio,
  Wallet, BarChart3, MapPin, Users, Trophy, Share2, PlayCircle, ShieldCheck, MessageSquareOff,
  Sparkles, ChevronDown, Instagram, CheckCircle2, UserPlus, ListChecks, Bell, Repeat, Zap, CreditCard,
} from "lucide-react";
import { api, money, APP_NAME } from "@/lib/api";
import { initAnalytics, loadMarketing, track, captureUTM } from "@/lib/analytics";

const YEAR = new Date().getFullYear();
const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(".", ",").replace(",0", "") + "k" : String(n));
const CONSENT_KEY = "mc_cookie_consent";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (u, fb) => { if (!u) return fb; if (u.startsWith("/api/")) return `${BACKEND}${u}`; return u; };
const setFavicon = (href) => { if (!href) return; let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); } l.href = href; };

const A = "https://customer-assets-lxgj4vgw.emergentagent.net/job_gig-connect-339/artifacts";
const ART = {
  hero: `${A}/xw5i5v1a_ChatGPT%20Image%2010%20de%20set.%20de%202026%2C%2001_20_44%20%281%29.png`,
  creators: `${A}/rcw8at02_ChatGPT%20Image%2010%20de%20set.%20de%202026%2C%2001_20_45%20%284%29.png`,
  finance: `${A}/cvcdpbvi_ChatGPT%20Image%2010%20de%20set.%20de%202026%2C%2001_20_44%20%283%29.png`,
  musicians: `${A}/tvi8wk8d_ChatGPT%20Image%2010%20de%20set.%20de%202026%2C%2001_20_46%20%2810%29.png`,
  repertoire: `${A}/hb78mm3h_ChatGPT%20Image%2010%20de%20set.%20de%202026%2C%2001_20_46%20%288%29.png`,
};

const ICONS = {
  MessageSquareOff, CalendarClock, Wallet, Library, Trophy, Radio, MapPin, BarChart3,
  Bell, Repeat, Zap, CreditCard, ShieldCheck, Share2, Users, Star, Music4, Guitar, Sparkles,
  CheckCircle2, UserPlus, ListChecks, PlayCircle,
};
const iconOf = (name) => ICONS[name] || Sparkles;

const DEF_PROBLEMS = [
  { icon: "MessageSquareOff", t: "Show organizado em 10 conversas", d: "Músicos, repertório, cachês e confirmações espalhados no WhatsApp." },
  { icon: "CalendarClock", t: "Agenda bagunçada", d: "Dois trabalhos no mesmo horário e oportunidades que você nem soube que existiram." },
  { icon: "Wallet", t: "Cachê sem controle", d: "Planilha, atraso em pagamentos e nenhuma visão do que entrou e do que falta repassar." },
  { icon: "Library", t: "Repertório perdido", d: "Cifra num grupo, tom em outro, ordem das músicas ninguém sabe qual é." },
];
const DEF_FEATURES = [
  { icon: "Trophy", t: "Construa sua reputação", d: "Cada evento e avaliação faz seu Score crescer — do Iniciante ao Profissional." },
  { icon: "CalendarClock", t: "Agenda sem conflitos", d: "Veja seus próximos shows e evite choques de horário automaticamente." },
  { icon: "Library", t: "Todo mundo na mesma música", d: "Repertórios, cifras, tom e ordem por evento — num só lugar." },
  { icon: "Radio", t: "Maestro em tempo real", d: "Sincronize a execução ao vivo durante o show." },
  { icon: "MapPin", t: "Check-in e check-out", d: "Confirme presença por localização e feche o evento do início ao fim." },
  { icon: "Wallet", t: "Cachê organizado", d: "Receba via Pix e acompanhe pago, recebido e a receber." },
  { icon: "BarChart3", t: "Financeiro claro", d: "Enxergue entradas, saídas e o resultado de cada evento." },
  { icon: "Bell", t: "Notificações e lembretes", d: "Convites, confirmações e avisos do evento sempre no seu radar." },
  { icon: "Repeat", t: "Recorrência e modelos", d: "Crie eventos repetidos e modelos para publicar em segundos." },
];
const DEF_TIMELINE = [
  { t: "Crie o evento", d: "Data, local, cachê e funções necessárias." },
  { t: "Informe os músicos", d: "Diga quais instrumentos você precisa." },
  { t: "Receba candidatos", d: "Profissionais compatíveis demonstram interesse." },
  { t: "Monte a equipe", d: "Escolha os músicos ideais para o show." },
  { t: "Organize o repertório", d: "Músicas, ordem, tom e maestro." },
  { t: "Confirme presença", d: "Acompanhe quem está confirmado." },
  { t: "Check-in no evento", d: "Confirmação por localização." },
  { t: "Execute o show", d: "Repertório sincronizado ao vivo." },
  { t: "Check-out", d: "Feche o evento com controle." },
  { t: "Faça os repasses", d: "Pague a equipe via Pix." },
  { t: "Avalie os participantes", d: "Reputação para todos." },
  { t: "Acompanhe resultados", d: "Financeiro e relatórios do evento." },
];
const DEF_CREATOR_BENEFITS = ["Crie eventos e defina as vagas", "Encontre músicos compatíveis", "Receba e selecione candidaturas", "Acompanhe confirmações e check-in", "Organize repertório e maestro", "Controle cachês, repasses e relatórios"];
const DEF_MUSICIAN_BENEFITS = ["Veja oportunidades compatíveis", "Candidate-se com poucos toques", "Agenda sem conflito de horário", "Check-in e acompanhamento de cachês", "Construa Score e receba avaliações", "Perfil profissional com vídeos e instrumentos"];
const DEF_SEGURANCA = [
  { icon: "ShieldCheck", t: "Pagamentos via Pix", d: "Cachês e repasses com status transparente." },
  { icon: "Zap", t: "Tempo real", d: "Repertório e maestro sincronizados no show." },
  { icon: "MapPin", t: "Check-in por localização", d: "Presença confirmada com geolocalização." },
  { icon: "CreditCard", t: "Comece sem cartão", d: "Teste o produto gratuitamente." },
];
const DEF_FAQ = [
  ["Preciso pagar para começar?", "Não. Você começa gratuitamente, testa a plataforma e faz upgrade quando precisar de mais recursos."],
  ["Posso usar apenas como músico?", "Sim. Crie seu perfil, encontre oportunidades compatíveis e candidate-se sem custo para começar."],
  ["Posso criar eventos?", "Sim. Como contratante você publica o evento, define as funções e recebe candidatos compatíveis."],
  ["Como funcionam os pagamentos?", "Cachês via Pix dentro da plataforma, com status de pago, recebido e a receber, e repasse direto."],
  ["Consigo organizar o repertório?", "Sim. Músicas, ordem, tom e maestro em tempo real — todo mundo tocando a mesma coisa."],
  ["Posso usar pelo celular?", "Sim. O Mí Chamou é feito para o celular, do primeiro cadastro ao check-in no palco."],
  ["Existe limite de eventos?", "O plano gratuito permite começar; os planos pagos ampliam limites e recursos avançados."],
  ["Como funciona o Score?", "Sua reputação cresce com perfil completo, eventos, avaliações, participação e convites."],
];
const DEF_ORDER = ["problema", "split", "finance", "repertoire", "how", "score", "recursos", "planos", "seguranca", "faq"];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setShown(true); return; }
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }), { threshold: 0.12 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(24px)", transition: `opacity .6s ease ${delay}ms, transform .6s cubic-bezier(.2,.7,.2,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

const ArtFrame = ({ src, alt, className = "" }) => (
  <div className={`relative rounded-3xl overflow-hidden border border-accent/25 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] ${className}`}>
    <div className="absolute -inset-6 bg-[radial-gradient(circle_at_50%_0%,rgba(255,122,0,0.25),transparent_65%)] pointer-events-none" aria-hidden="true" />
    <img src={src} alt={alt} loading="lazy" className="relative w-full h-full object-cover" />
  </div>
);

export default function Landing({ preview }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [branding, setBranding] = useState(null);
  const [consent, setConsent] = useState(() => { try { return localStorage.getItem(CONSENT_KEY); } catch (e) { return null; } });

  useEffect(() => {
    captureUTM();
    track("page_view", { page: "landing" });
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
    api.get(preview ? "/admin/landing" : "/public/landing").then((r) => {
      setCfg(r.data);
      if (r.data?.seo_title) document.title = r.data.seo_title;
      const fav = asset(r.data?.favicon) || asset(r.data?.img_logo);
      if (fav) setFavicon(fav);
      initAnalytics(r.data?.analytics, consent === "all");
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "dark");
    return () => { if (prev) document.documentElement.setAttribute("data-theme", prev); };
  }, []);

  const acceptCookies = (all) => {
    try { localStorage.setItem(CONSENT_KEY, all ? "all" : "essential"); } catch (e) {}
    setConsent(all ? "all" : "essential");
    if (all && cfg?.analytics) loadMarketing(cfg.analytics);
  };

  const t = (k, fb) => (cfg && cfg[k]) || fb;
  const sec = (k) => !cfg || !cfg.sections || cfg.sections[k] !== false;
  const list = (k, fb) => (cfg && Array.isArray(cfg[k]) && cfg[k].length > 0 ? cfg[k] : fb);
  const faqItems = (cfg?.faq && cfg.faq.length > 0) ? cfg.faq.map((f) => [f.q, f.a]) : DEF_FAQ;
  const go = (path, ev, data) => { track(ev, data); navigate(path); };
  const qs = () => (typeof window !== "undefined" ? window.location.search.replace(/^\?/, "") : "");
  const reg = (type) => { const s = qs(); const p = [type ? `type=${type}` : "", s].filter(Boolean).join("&"); return `/register${p ? `?${p}` : ""}`; };
  const NAV = [["como-funciona", "Como funciona"], ["musicos", "Para músicos"], ["contratantes", "Para contratantes"], ["recursos", "Recursos"], ["planos", "Planos"], ["faq", "FAQ"]];
  const scrollTo = (id) => { setMenu(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  const recommended = plans.find((p) => p.slug === cfg?.recommended_plan) || plans.find((p) => p.slug === "pro_plus") || plans[1];
  const logo = asset(cfg?.img_logo) || asset(branding?.logo_main) || "/michamou-icon.png";
  const logomarca = asset(cfg?.img_logomarca);
  const order = (cfg?.section_order && cfg.section_order.length > 0) ? cfg.section_order : DEF_ORDER;

  const problems = list("problems", DEF_PROBLEMS);
  const features = list("features", DEF_FEATURES);
  const timeline = list("timeline", DEF_TIMELINE);
  const creatorBenefits = list("creator_benefits", DEF_CREATOR_BENEFITS);
  const musicianBenefits = list("musician_benefits", DEF_MUSICIAN_BENEFITS);
  const segurancaCards = list("seguranca_cards", DEF_SEGURANCA);

  const headBtn = "inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]";
  const ctaPrimary = `${headBtn} px-6 py-3.5 bg-gradient-to-r from-[#FF4D00] to-accent text-white shadow-[0_0_28px_rgba(255,79,0,0.35)] hover:shadow-[0_0_40px_rgba(255,79,0,0.5)] hover:-translate-y-0.5`;
  const ctaGhost = `${headBtn} px-6 py-3.5 bg-white/5 border border-white/15 text-white hover:bg-white/10`;

  const SECTIONS = {
    problema: () => (
      <section key="problema" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06]">
        <Reveal>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">{t("problema_title", "Chega de organizar show em dez conversas diferentes.")}</h2>
          <p className="mt-3 text-zinc-400 max-w-2xl">{t("problema_sub", "Você reconhece alguma dessas cenas do dia a dia?")}</p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {problems.map((p, i) => { const Ic = iconOf(p.icon); return (
            <Reveal key={i} delay={i * 70}>
              <div className="h-full rounded-2xl border border-white/[0.07] bg-[#121217] p-5">
                <div className="h-11 w-11 rounded-xl bg-accent/10 flex items-center justify-center mb-3"><Ic className="h-5 w-5 text-accent" /></div>
                <h3 className="font-semibold">{p.t}</h3>
                <p className="text-sm text-zinc-400 mt-1">{p.d}</p>
              </div>
            </Reveal>
          ); })}
        </div>
        <Reveal className="mt-8">
          <p className="text-lg text-zinc-200">{t("problema_footer", "Agora imagine tudo isso organizado em um único lugar. É o Mí Chamou.")}</p>
        </Reveal>
      </section>
    ),
    split: () => (
      <section key="split" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06] grid lg:grid-cols-2 gap-6">
        <Reveal>
          <div id="contratantes" className="h-full rounded-3xl border border-white/[0.07] bg-[#121217] overflow-hidden scroll-mt-20">
            <ArtFrame src={asset(cfg?.img_creators, ART.creators)} alt="Para criadores de eventos" className="rounded-none border-0 border-b border-white/10 aspect-[4/3]" />
            <div className="p-6">
              <span className="text-xs font-bold uppercase tracking-widest text-accent">{t("split_creators_kicker", "Para criadores de eventos")}</span>
              <h3 className="font-head text-2xl font-bold mt-1">{t("split_creators_title", "Monte sua equipe sem dor de cabeça")}</h3>
              <ul className="mt-4 space-y-2">
                {creatorBenefits.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-zinc-300"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />{b}</li>)}
              </ul>
              <button onClick={() => go(reg("contratante"), "contractor_cta_click", { cta: "split" })} data-testid="creators-cta" className={`${ctaPrimary} mt-6 w-full`}><Guitar className="h-5 w-5" />{t("split_creators_cta", "Criar meu evento")}</button>
            </div>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div id="musicos" className="h-full rounded-3xl border border-white/[0.07] bg-[#121217] overflow-hidden scroll-mt-20">
            <ArtFrame src={asset(cfg?.img_musicians, ART.musicians)} alt="Descubra eventos e oportunidades" className="rounded-none border-0 border-b border-white/10 aspect-[4/3]" />
            <div className="p-6">
              <span className="text-xs font-bold uppercase tracking-widest text-accent">{t("split_musicians_kicker", "Para músicos")}</span>
              <h3 className="font-head text-2xl font-bold mt-1">{t("split_musicians_title", "Seu talento encontra mais palcos")}</h3>
              <ul className="mt-4 space-y-2">
                {musicianBenefits.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-zinc-300"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />{b}</li>)}
              </ul>
              <button onClick={() => go(reg("musico"), "musician_cta_click", { cta: "split" })} data-testid="musicians-cta" className={`${ctaPrimary} mt-6 w-full`}><Music4 className="h-5 w-5" />{t("split_musicians_cta", "Criar meu perfil")}</button>
            </div>
          </div>
        </Reveal>
      </section>
    ),
    finance: () => (
      <section key="finance" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06] grid lg:grid-cols-2 gap-10 items-center">
        <Reveal><ArtFrame src={asset(cfg?.img_finance, ART.finance)} alt="Financeiro e relatórios do Mí Chamou" /></Reveal>
        <Reveal delay={100}>
          <span className="text-xs font-bold uppercase tracking-widest text-accent">{t("finance_kicker", "Financeiro")}</span>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("finance_title", "Do cachê ao repasse. Sem planilha. Sem confusão.")}</h2>
          <p className="mt-3 text-zinc-400">{t("finance_desc", "Saiba quanto entrou, quanto precisa repassar e o que ainda está pendente — tudo com Pix integrado.")}</p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[["Entradas", Wallet], ["Repasses", Share2], ["Relatórios", BarChart3]].map(([l, Ic]) => (
              <div key={l} className="rounded-xl border border-white/10 bg-[#121217] p-3 text-center"><Ic className="h-5 w-5 text-accent mx-auto" /><div className="text-xs text-zinc-400 mt-1.5">{l}</div></div>
            ))}
          </div>
        </Reveal>
      </section>
    ),
    repertoire: () => (
      <section key="repertoire" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06] grid lg:grid-cols-2 gap-10 items-center">
        <Reveal className="lg:order-2"><ArtFrame src={asset(cfg?.img_repertoire, ART.repertoire)} alt="Repertório sincronizado com maestro em tempo real" /></Reveal>
        <Reveal delay={100} className="lg:order-1">
          <span className="text-xs font-bold uppercase tracking-widest text-accent">{t("repertoire_kicker", "Repertório")}</span>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("repertoire_title", "Todo mundo tocando a mesma música. Literalmente.")}</h2>
          <p className="mt-3 text-zinc-400">{t("repertoire_desc", "Músicas, ordem, tom e maestro em tempo real. Menos desordem, mais música.")}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {["Músicas organizadas", "Ordem do repertório", "Maestro ao vivo", "Compartilhado"].map((c) => (
              <span key={c} className="text-xs px-3 py-1.5 rounded-full border border-accent/30 text-accent/90">{c}</span>
            ))}
          </div>
        </Reveal>
      </section>
    ),
    how: () => (
      <section key="how" id="como-funciona" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06] scroll-mt-16">
        <Reveal>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-center">{t("how_title", "Do evento ao pagamento")}</h2>
          <p className="mt-2 text-center text-zinc-400 max-w-2xl mx-auto">{t("how_sub", "Acompanhe o show inteiro dentro da mesma plataforma — do primeiro clique ao último repasse.")}</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {timeline.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 60}>
              <div className="h-full rounded-2xl border border-white/[0.07] bg-[#121217] p-4 flex gap-3">
                <span className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-[#FF4D00] to-accent flex items-center justify-center font-head font-bold text-sm">{i + 1}</span>
                <div><div className="font-semibold text-sm">{s.t}</div><div className="text-xs text-zinc-400 mt-0.5">{s.d}</div></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    ),
    score: () => (
      <section key="score" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06]">
        <Reveal>
          <div className="rounded-3xl border border-accent/25 bg-[radial-gradient(circle_at_20%_0%,rgba(255,79,0,0.14),transparent_60%)] bg-[#121217] p-8 lg:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-accent">{t("score_kicker", "Score profissional")}</span>
              <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("score_title", "Seu trabalho constrói sua reputação.")}</h2>
              <p className="mt-3 text-zinc-400">{t("score_desc", "Cada evento realizado, avaliação positiva e participação faz seu perfil evoluir — do Iniciante ao Profissional. Transforme o Mí Chamou na sua identidade no mercado da música.")}</p>
              <button onClick={() => go(reg("musico"), "musician_cta_click", { cta: "score" })} className={`${ctaPrimary} mt-6`}><Trophy className="h-5 w-5" />{t("score_cta", "Começar meu perfil")}</button>
            </div>
            <div className="flex items-center justify-center gap-6">
              {[["Iniciante", 40], ["Intermediário", 70], ["Profissional", 96]].map(([l, v]) => (
                <div key={l} className="text-center">
                  <div className="relative h-24 w-24 rounded-full grid place-items-center" style={{ background: `conic-gradient(var(--accent) ${v}%, rgba(255,255,255,0.08) 0)` }}>
                    <div className="h-[76px] w-[76px] rounded-full bg-[#121217] grid place-items-center font-head font-bold text-lg">{v}</div>
                  </div>
                  <div className="text-xs text-zinc-400 mt-2">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    ),
    recursos: () => (
      <section key="recursos" id="recursos" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06] scroll-mt-16">
        <Reveal>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-center">{t("features_title", "Tudo o que o show precisa")}</h2>
          <p className="mt-2 text-center text-zinc-400">{t("features_sub", "Recursos pensados para quem vive de música e eventos.")}</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => { const Ic = iconOf(f.icon); return (
            <Reveal key={i} delay={(i % 3) * 60}>
              <div className="h-full rounded-2xl border border-white/[0.07] bg-[#121217] p-5 hover:border-accent/40 transition-colors">
                <div className="h-11 w-11 rounded-xl bg-accent/10 flex items-center justify-center mb-3"><Ic className="h-5 w-5 text-accent" /></div>
                <h3 className="font-semibold">{f.t}</h3>
                <p className="text-sm text-zinc-400 mt-1">{f.d}</p>
              </div>
            </Reveal>
          ); })}
        </div>
      </section>
    ),
    planos: () => (
      <section key="planos" id="planos" className="max-w-6xl mx-auto px-4 py-16 border-t border-white/[0.06] scroll-mt-16">
        <Reveal>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-center">{t("planos_title", "Comece grátis. Evolua quando precisar.")}</h2>
          <p className="mt-2 text-center text-zinc-400 text-sm">{t("planos_sub", "Sem cartão para começar. Faça upgrade conforme sua necessidade.")}</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p, i) => {
            const rec = recommended && p.slug === recommended.slug;
            const free = (p.price || 0) === 0;
            return (
              <Reveal key={p.slug} delay={(i % 4) * 60}>
                <div data-testid={`plan-card-${p.slug}`} className={`relative h-full rounded-2xl p-5 flex flex-col border ${rec ? "border-[#FF4D00] bg-[#FF4D00]/[0.06] shadow-[0_0_30px_rgba(255,79,0,0.2)]" : "border-white/[0.07] bg-[#121217]"}`}>
                  {rec && <span className="absolute -top-2.5 left-4 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-[#FF4D00]">Recomendado</span>}
                  <div className="font-head text-xl font-bold">{p.name}</div>
                  <div className="mt-2 flex items-end gap-1">
                    <span className="text-3xl font-bold font-mono">{free ? "Grátis" : money(p.price)}</span>
                    {!free && <span className="text-xs text-zinc-500 mb-1">/mês</span>}
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">{free ? "1 evento (único)" : p.event_limit < 0 ? "Eventos ilimitados" : `${p.event_limit} evento(s)/mês`}</div>
                  <ul className="mt-4 space-y-1.5 flex-1">
                    {(p.features || []).slice(0, 6).map((f, k) => <li key={k} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />{f}</li>)}
                  </ul>
                  <button onClick={() => go(free ? reg() : `/register?plan=${p.slug}${qs() ? `&${qs()}` : ""}`, free ? "hero_cta_click" : "plan_select", { plan: p.slug })} data-testid={`plan-cta-${p.slug}`} className={`mt-5 py-3 rounded-full font-semibold transition-colors ${rec ? "bg-[#FF4D00] hover:bg-[#E64500]" : "bg-white/5 border border-white/10 hover:bg-white/10"}`}>{free ? "Começar grátis" : "Assinar"}</button>
                </div>
              </Reveal>
            );
          })}
        </div>
        {plans.length === 0 && <p className="text-center text-zinc-600 text-sm mt-6">Carregando planos...</p>}
      </section>
    ),
    seguranca: () => (
      <section key="seguranca" className="max-w-6xl mx-auto px-4 py-12 border-t border-white/[0.06]">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {segurancaCards.map((c, i) => { const Ic = iconOf(c.icon); return (
            <Reveal key={i}><div className="h-full rounded-2xl border border-white/[0.07] bg-[#121217] p-5"><Ic className="h-6 w-6 text-accent" /><div className="font-semibold mt-2">{c.t}</div><div className="text-xs text-zinc-400 mt-1">{c.d}</div></div></Reveal>
          ); })}
        </div>
        {stats && stats.users >= 50 && (
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            {[["Usuários", stats.users], ["Músicos", stats.musicians], ["Eventos", stats.events], ["Avaliações", stats.reviews]].map(([l, v]) => (
              <div key={l} className="bg-[#121217] border border-white/[0.06] rounded-2xl p-5"><div className="font-mono text-3xl font-bold text-[#FF4D00]">{v}</div><div className="text-xs text-zinc-500 mt-1">{l}</div></div>
            ))}
          </div>
        )}
      </section>
    ),
    faq: () => (
      <section key="faq" id="faq" className="max-w-3xl mx-auto px-4 py-16 border-t border-white/[0.06] scroll-mt-16">
        <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-center">Perguntas frequentes</h2>
        <div className="mt-8 space-y-2" data-testid="landing-faq">
          {faqItems.map(([q, a], i) => (
            <div key={i} className="bg-[#121217] border border-white/[0.06] rounded-xl overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left font-medium" data-testid={`faq-q-${i}`} aria-expanded={openFaq === i}>
                {q}<ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <div className="px-4 pb-4 text-sm text-zinc-400">{a}</div>}
            </div>
          ))}
        </div>
      </section>
    ),
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white antialiased selection:bg-accent/30" data-testid="landing-page">
      {preview && <div data-testid="preview-banner" className="fixed top-2 left-1/2 -translate-x-1/2 z-[80] text-xs px-3 py-1 rounded-full bg-amber-500 text-black font-bold shadow-lg">Pré-visualização (rascunho)</div>}
      {/* HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#09090B]/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <button onClick={() => scrollTo("topo")} className="flex items-center gap-2" data-testid="landing-logo" aria-label={APP_NAME}>
            {logomarca ? (
              <img src={logomarca} alt={APP_NAME} className="h-8 w-auto object-contain" />
            ) : (
              <>
                <img src={logo} alt="Mí Chamou" className="h-8 w-8 rounded-lg object-cover" />
                <span className="font-head font-extrabold text-xl tracking-tight">{APP_NAME}</span>
              </>
            )}
          </button>
          <nav className="hidden lg:flex items-center gap-6 text-sm text-zinc-300" aria-label="Seções">
            {NAV.map(([id, l]) => <button key={id} onClick={() => scrollTo(id)} className="hover:text-white transition-colors">{l}</button>)}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <button onClick={() => go("/login", "login")} data-testid="header-login" className="px-4 py-2 text-sm text-zinc-300 hover:text-white">Entrar</button>
            <button onClick={() => go(reg(), "hero_cta_click", { cta: "header" })} data-testid="header-signup" className={`${headBtn} px-4 py-2 text-sm bg-[#FF4D00] hover:bg-[#E64500]`}>Começar agora</button>
          </div>
          <button onClick={() => setMenu((m) => !m)} className="lg:hidden p-2 -mr-2" data-testid="mobile-menu-btn" aria-label="Menu" aria-expanded={menu}>{menu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
        </div>
        {menu && (
          <div className="lg:hidden border-t border-white/[0.06] bg-[#09090B] px-4 py-4 space-y-1" data-testid="mobile-menu">
            {NAV.map(([id, l]) => <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left py-2 text-zinc-300">{l}</button>)}
            <button onClick={() => go("/login", "login")} className="block w-full text-left py-2 text-zinc-300">Entrar</button>
            <button onClick={() => go(reg(), "hero_cta_click", { cta: "menu" })} className="mt-2 w-full py-3 text-center font-semibold rounded-full bg-[#FF4D00]">Começar agora</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="topo" className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(255,79,0,0.28),transparent_45%),radial-gradient(circle_at_90%_10%,rgba(255,160,0,0.18),transparent_40%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 pt-14 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent"><Sparkles className="h-3.5 w-3.5" />{t("hero_badge", "Música conecta pessoas")}</span>
            <h1 className="mt-5 font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.03]">
              {t("hero_title", "Sua música. Seus eventos.")}<br /><span className="bg-gradient-to-r from-[#FF4D00] to-accent bg-clip-text text-transparent">Sua equipe. Num só lugar.</span>
            </h1>
            <p className="mt-5 text-lg text-zinc-300 max-w-xl">{t("hero_subtitle", "A plataforma que conecta músicos freelancers e criadores de eventos — contratação, agenda, repertório, pagamentos e gestão completa do show.")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => go(reg("musico"), "musician_cta_click", { cta: "hero" })} data-testid="hero-musico-cta" className={ctaPrimary}><Music4 className="h-5 w-5" />Encontrar eventos</button>
              <button onClick={() => go(reg("contratante"), "contractor_cta_click", { cta: "hero" })} data-testid="hero-contratante-cta" className={ctaGhost}><Guitar className="h-5 w-5" />Contratar músicos</button>
            </div>
            <button onClick={() => go("/login", "login")} data-testid="hero-login" className="mt-4 text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">Já tenho conta — Entrar <ArrowRight className="h-4 w-4" /></button>
            {stats && (stats.musicians > 0 || stats.events > 0) && (
              <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4" data-testid="hero-social-proof">
                {[[Users, stats.musicians, "músicos"], [CalendarClock, stats.events, "eventos"], [Star, stats.reviews, "avaliações"]].filter((x) => x[1] > 0).map(([Ic, n, label]) => (
                  <div key={label} className="flex items-center gap-2.5">
                    <Ic className="h-5 w-5 text-accent" aria-hidden="true" />
                    <span className="font-head font-bold text-2xl">+{fmt(n)}</span>
                    <span className="text-sm text-zinc-400">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Reveal className="hidden lg:block">
            <ArtFrame src={asset(cfg?.img_hero, ART.hero)} alt="Mí Chamou conectando músicos e criadores de eventos" className="rotate-1 hover:rotate-0 transition-transform duration-500" />
          </Reveal>
        </div>
      </section>

      {/* SEÇÕES DINÂMICAS */}
      {order.filter((id) => SECTIONS[id] && sec(id)).map((id) => SECTIONS[id]())}

      {/* CTA FINAL */}
      <section className="max-w-6xl mx-auto px-4 py-20 border-t border-white/[0.06]">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden p-10 lg:p-16 text-center bg-gradient-to-br from-[#FF4D00] to-[#b83700]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(0,0,0,0.35),transparent_60%)]" aria-hidden="true" />
            <div className="relative">
              <h2 className="font-head text-3xl sm:text-5xl font-extrabold tracking-tight">{t("final_title", "A música acontece no palco. A organização começa aqui.")}</h2>
              <p className="mt-3 text-white/90 max-w-xl mx-auto">{t("final_sub", `Entre para o ${APP_NAME} e transforme a forma como você encontra músicos, oportunidades e organiza seus eventos.`)}</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => go(reg(), "hero_cta_click", { cta: "final" })} data-testid="final-cta" className={`${headBtn} px-7 py-3.5 bg-[#09090B] text-white hover:bg-black`}>Começar agora <ArrowRight className="h-5 w-5" /></button>
                <button onClick={() => go(reg("musico"), "musician_cta_click", { cta: "final" })} className={`${headBtn} px-7 py-3.5 bg-white/15 border border-white/30 text-white hover:bg-white/25`}>Criar meu perfil</button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06] bg-[#09090B]">
        <div className="max-w-6xl mx-auto px-4 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2">{logomarca ? <img src={logomarca} alt={APP_NAME} className="h-8 w-auto object-contain" /> : <><img src={logo} alt="" className="h-8 w-8 rounded-lg object-cover" /><span className="font-head font-extrabold text-lg">{APP_NAME}</span></>}</div>
            <p className="mt-3 text-zinc-500">{t("footer_desc", "Música conecta pessoas. Oportunidades, músicos e eventos em um só lugar.")}</p>
          </div>
          <div><div className="text-zinc-300 font-semibold mb-3">Produto</div><ul className="space-y-2 text-zinc-500">{NAV.map(([id, l]) => <li key={id}><button onClick={() => scrollTo(id)} className="hover:text-white">{l}</button></li>)}</ul></div>
          <div><div className="text-zinc-300 font-semibold mb-3">Conta</div><ul className="space-y-2 text-zinc-500"><li><button onClick={() => go("/login", "login")} className="hover:text-white">Entrar</button></li><li><button onClick={() => go(reg(), "hero_cta_click", { cta: "footer" })} className="hover:text-white">Criar conta grátis</button></li></ul></div>
          <div>
            <div className="text-zinc-300 font-semibold mb-3">App</div>
            <div className="flex flex-col gap-2 text-zinc-500">
              {cfg?.play_store ? <a href={cfg.play_store} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-sm bg-[#121217] border border-white/10 w-fit hover:border-accent/40">Google Play</a> : <span className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-sm bg-[#121217] border border-white/10 w-fit">Google Play — Em breve</span>}
              {cfg?.app_store ? <a href={cfg.app_store} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-sm bg-[#121217] border border-white/10 w-fit hover:border-accent/40">App Store</a> : <span className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-sm bg-[#121217] border border-white/10 w-fit">App Store — Em breve</span>}
            </div>
            <div className="mt-4 flex gap-3">
              {cfg?.instagram && <a href={cfg.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-9 w-9 rounded-lg bg-[#121217] border border-white/10 flex items-center justify-center text-zinc-400 hover:text-accent"><Instagram className="h-4 w-4" /></a>}
            </div>
          </div>
        </div>
        <div className="border-t border-white/[0.06] py-5 text-center text-xs text-zinc-600">
          <div className="flex flex-wrap gap-4 justify-center mb-3 text-zinc-500">
            <a href="/termos" className="hover:text-white">Termos</a>
            <a href="/privacidade" className="hover:text-white">Privacidade</a>
            <a href="/cookies" className="hover:text-white">Cookies</a>
          </div>
          © {YEAR} {APP_NAME}. Todos os direitos reservados.
        </div>
      </footer>

      {!consent && (
        <div className="fixed bottom-0 inset-x-0 z-[60] bg-[#121217] border-t border-white/10 p-4" data-testid="cookie-consent">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-3 justify-between">
            <p className="text-sm text-zinc-400">Usamos cookies para medição e marketing. Você pode aceitar todos ou apenas os essenciais. Veja a <a href="/cookies" className="text-accent hover:underline">Política de Cookies</a>.</p>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => acceptCookies(false)} data-testid="cookie-essential" className="px-4 py-2 text-sm rounded-full bg-white/5 border border-white/10 hover:bg-white/10">Só essenciais</button>
              <button onClick={() => acceptCookies(true)} data-testid="cookie-accept" className="px-4 py-2 text-sm font-semibold rounded-full bg-[#FF4D00] hover:bg-[#E64500]">Aceitar todos</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
